import React from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import { useSnackbarContext } from 'Context/SnackbarContext';

const AutoAllocateAllContext = React.createContext(undefined);

// Actions
export const ALLOCATE_START = 'allocate-started';
export const ALLOCATE_SUCCEEDED = 'allocate-succeeded';
export const ALLOCATE_FAILED = 'allocate-failed';

const init = () => {
  return {
    isAutoAllocating: false,
    allocatingError: null,
    allocatingResponse: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case ALLOCATE_START: {
      return {
        ...state,
        isAutoAllocating: true,
        allocatingError: null,
      };
    }
    case ALLOCATE_SUCCEEDED: {
      return {
        ...state,
        isAutoAllocating: false,
        allocatingError: null,
      };
    }
    case ALLOCATE_FAILED: {
      return {
        ...state,
        isAutoAllocating: false,
        allocatingError: action.allocatingError,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
};

const AutoAllocateAllProvider = ({
  children,
  allocateUrl,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, init);
  const { displayErrorMessage, displaySuccess } = useSnackbarContext();

  const handleAutoAllocate = React.useCallback(async (onSuccess) => {
    try {
      dispatch({ type: ALLOCATE_START });
      const resp = await fr.http.Post(allocateUrl);
      if (typeof onSuccess === 'function') {
        await onSuccess();
      }
      dispatch({ type: ALLOCATE_SUCCEEDED, allocatingResponse: resp });
      displaySuccess('Successfully auto allocated all outcomes');
    } catch (err) {
      dispatch({ type: ALLOCATE_FAILED, allocatingError: err });
      displayErrorMessage(err, 'Failed to auto allocate outcomes');
    }
  }, [state]);

  const memoizedValue = React.useMemo(() => {
    return {
      isAutoAllocating: state.isAutoAllocating,
      allocatingError: state.allocatingError,
      allocatingResponse: state.allocatingResponse,
      handleAutoAllocate,
    };
  }, [
    state,
    handleAutoAllocate,
  ]);

  return (
    <AutoAllocateAllContext.Provider value={memoizedValue}>
      {children}
    </AutoAllocateAllContext.Provider>
  );
};

const useAutoAllocateContext = () => {
  const context = React.useContext(AutoAllocateAllContext);
  if (context === undefined) {
    throw new Error('useAutoAllocateContext must be used within a AutoAllocateProvider');
  }
  return context;
};

AutoAllocateAllProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  allocateUrl: PropTypes.string.isRequired,
};

export {
  AutoAllocateAllProvider,
  useAutoAllocateContext,
};
